/* eslint-disable @typescript-eslint/no-explicit-any */
const HEADER_BG = '#8B2020';
const ACCENT = '#C0392B';
const TEXT = '#1a2340';

interface ComparableData {
  address?: string;
  soldPrice?: number;
  soldDate?: string;
  livingArea?: number;
  bedrooms?: number;
  bathrooms?: number;
  yearBuilt?: number;
  lotSize?: number;
  lotUnit?: string;
  garageType?: string;
  garageSpaces?: number;
  pool?: boolean;
  basement?: string;
  apn?: string;
  adjustmentValue?: number;
  livingAreaAdjustment?: number;
  conditionAdjustment?: number | string;
  locationAdjustment?: number | string;
  garageAdjustment?: number | string;
  dateAdjustment?: number | string;
  otherFeaturesAdjustment?: number | string;
  notes?: string;
  mls?: Record<string, any>;
}

export interface AppealPdfData {
  finalValuation: number;
  marketFairValue?: string;
  ownerName: string;
  ownerEmail?: string;
  ownerPhone?: string;
  address: string;
  comparables?: ComparableData[];
  aiComparables?: ComparableData[];
  propertyInfo?: Record<string, any>;
  images?: string[];
  staticAppeal?: Record<string, any>;
  staticInstructions?: Record<string, any>;
}

function formatCurrency(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(Number(value)))
    return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function formatSoldDate(dateStr?: string): string {
  if (!dateStr) return 'N/A';
  const parts = dateStr.split('-');
  if (parts.length < 2) return dateStr;
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const month = parseInt(parts[1], 10);
  const year = parts[0];
  return `${months[month - 1] || ''} ${year}`;
}

function getPhotos(mls: Record<string, any> | undefined, count = 4): string[] {
  if (!mls?.Media || !Array.isArray(mls.Media)) return [];
  return [...mls.Media]
    .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
    .slice(0, count)
    .map((m: any) => m.MediaURL)
    .filter(Boolean);
}

function parseAdjNum(v?: string | number): number {
  if (v === undefined || v === null || v === '' || v === 'N/A') return 0;
  return typeof v === 'number' ? v : parseFloat(v as string) || 0;
}

function formatAdj(value?: string | number): string {
  if (value === undefined || value === null || value === '' || value === 'N/A')
    return '—';
  if (typeof value === 'number') {
    if (value === 0) return '—';
    const abs = formatCurrency(Math.abs(value));
    return value > 0 ? `+${abs}` : `-${abs}`;
  }
  return String(value);
}

function extractCounty(address: string): string {
  const match = address.match(/([A-Za-z]+(?:\s+[A-Za-z]+)*\s+County)/i);
  return match ? match[1] : 'County';
}

function extractState(address: string): string {
  const match = address.match(/,\s*([A-Z]{2})[\s,]/);
  return match ? match[1] : '';
}

function pageHeader(pageInfo?: string): string {
  return `<div style="background:${HEADER_BG};color:white;padding:7px 22px;display:flex;justify-content:space-between;align-items:center;font-size:8px;font-weight:bold;letter-spacing:0.5px;">
    <span>PROPERTY TAX VALUATION APPEAL</span>
    <span>${pageInfo ?? ''}</span>
  </div>`;
}

function pageFooter(): string {
  return `<div style="text-align:center;font-size:8px;color:#aaa;padding:8px 50px 12px;border-top:1px solid #eee;margin-top:auto;">
    Review all values with your county assessor's office before filing.
  </div>`;
}

function sectionTitle(text: string): string {
  return `<div style="color:${ACCENT};font-size:12px;font-weight:bold;border-bottom:2px solid ${ACCENT};padding-bottom:4px;margin:18px 0 12px;">${text}</div>`;
}

function photosGrid(urls: string[]): string {
  if (!urls.length) return '';
  return `<div style="display:flex;gap:6px;margin-bottom:18px;">
    ${urls.map((url) => `<div style="flex:1;overflow:hidden;border:1px solid #ddd;"><img src="${url}" onerror="this.parentElement.style.display='none'" style="width:100%;height:100px;object-fit:cover;display:block;" /></div>`).join('')}
  </div>`;
}

function detailCell(label: string, value: string): string {
  return `<div style="padding:5px 0;">
    <div style="font-size:8px;color:#999;margin-bottom:3px;">${label}</div>
    <div style="font-size:12px;font-weight:bold;color:${TEXT};">${value}</div>
  </div>`;
}

function instructionsPage(data: AppealPdfData): string {
  const county =
    data.staticInstructions?.countyName ?? extractCounty(data.address);
  const phone = data.staticInstructions?.phone ?? '';
  const website = data.staticInstructions?.websiteUrl ?? '';
  const addressLines: string[] = data.staticInstructions?.addressLines ?? [];

  return `<div style="page-break-after:always;background:white;font-family:Arial,Helvetica,sans-serif;color:${TEXT};height:calc(297mm - 1px);overflow:hidden;display:flex;flex-direction:column;box-sizing:border-box;">
  ${pageHeader()}
  <div style="flex:1;overflow:hidden;padding:50px 55px 20px;">
    <h1 style="font-size:26px;font-weight:bold;color:${TEXT};border-bottom:3px solid ${ACCENT};padding-bottom:8px;margin-bottom:8px;">Instructions to Property Owner</h1>
    <p style="color:#777;font-size:10px;margin-bottom:28px;">Please read carefully before filing your valuation appeal.</p>
    <div style="margin-bottom:18px;"><p style="font-weight:bold;font-size:11px;margin-bottom:5px;">Disclaimer</p><p style="font-size:10px;color:#444;line-height:1.65;">Verify all protest deadlines, procedures, and required documentation with the ${county} Assessor&apos;s Office. Local rules and requirements may change; compliance is your responsibility.</p></div>
    <div style="margin-bottom:18px;"><p style="font-weight:bold;font-size:11px;margin-bottom:5px;">Review and Edit</p><p style="font-size:10px;color:#444;line-height:1.65;">Carefully review and edit this draft before submission. Attach supporting documents (photos, appraisals, or additional comparable sales) as required by ${county} Assessor&apos;s Office.</p></div>
    <div style="margin-bottom:18px;"><p style="font-weight:bold;font-size:11px;margin-bottom:5px;">Legal Reference</p><p style="font-size:10px;color:#444;line-height:1.65;">For more information, visit the ${county} Assessor&apos;s Office website${website ? ': ' + website : ''}.</p></div>
    <div style="margin-bottom:18px;"><p style="font-weight:bold;font-size:11px;margin-bottom:5px;">How to File</p><p style="font-size:10px;color:#444;line-height:1.65;">You may file your protest online, by mail, or in person at the ${county} Assessor&apos;s Office. Check the website or call for current instructions and deadlines.</p></div>
    <div style="margin-bottom:28px;"><p style="font-weight:bold;font-size:11px;margin-bottom:5px;">Professional Advice</p><p style="font-size:10px;color:#444;line-height:1.65;">If you have questions about your property&apos;s value or the appeal process, consider consulting a licensed real estate appraiser or your real estate agent.</p></div>
    <table style="width:100%;border-collapse:collapse;border:1px solid #ddd;">
      <thead><tr style="border-bottom:2px solid ${ACCENT};"><td colspan="2" style="padding:10px 15px;color:${ACCENT};font-weight:bold;font-size:12px;">Assessor Contact Information</td></tr></thead>
      <tbody>
        <tr style="border-bottom:1px solid #eee;"><td style="padding:8px 15px;font-weight:bold;font-size:10px;">${county} Assessor&apos;s Office</td><td style="padding:8px 15px;font-size:10px;color:#555;">${phone ? 'Phone: ' + phone : ''}</td></tr>
        <tr><td style="padding:8px 15px;font-size:10px;color:#555;">${addressLines.join(', ')}</td><td style="padding:8px 15px;font-size:10px;color:#555;">${website}</td></tr>
      </tbody>
    </table>
  </div>
  ${pageFooter()}
</div>`;
}

function coverLetterPage(data: AppealPdfData, totalPages: number): string {
  const county = data.staticAppeal?.countyName ?? extractCounty(data.address);
  const officeName =
    data.staticAppeal?.assessorOfficeName ?? `${county} Assessor&apos;s Office`;
  const addressLines: string[] =
    data.staticAppeal?.assessorAddressLines ??
    data.staticInstructions?.addressLines ??
    [];
  const apn = data.staticAppeal?.apn ?? '';
  const legalDesc = data.staticAppeal?.legalDescription ?? '';
  const fmv = data.marketFairValue ?? formatCurrency(data.finalValuation);
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const propInfo = data.propertyInfo ?? {};
  const bedroomsVal = propInfo.bedrooms ?? propInfo.BedroomsTotal ?? '';
  const bathroomsVal =
    propInfo.bathrooms ?? propInfo.BathroomsTotalInteger ?? '';
  const areaVal = propInfo.livingArea ?? propInfo.LivingArea ?? '';
  const builtVal = propInfo.yearBuilt ?? propInfo.YearBuilt ?? '';
  const lotVal = propInfo.lotSize ?? propInfo.LotSizeArea ?? '';
  const lotUnit = propInfo.lotUnit ?? 'sq ft';

  const descParts = [
    bedroomsVal ? `${bedroomsVal} bedroom` : '',
    bathroomsVal ? `${bathroomsVal} bathroom` : '',
    areaVal
      ? `${Number(areaVal).toLocaleString()} sq ft of above-grade living area`
      : '',
    builtVal ? `built in ${builtVal}` : '',
    lotVal ? `on a ${Number(lotVal).toLocaleString()} ${lotUnit} lot` : '',
  ].filter(Boolean);

  const propDescription = descParts.length
    ? `<p style="font-size:10px;color:#333;line-height:1.7;margin-bottom:15px;text-align:justify;">The subject property is ${descParts.join(', ')}.</p>`
    : '';

  const corrections = (propInfo.propertyCorrections ?? {}) as Record<string, any>;
  const correctionItems = [
    corrections.livingAreaSqft ? `Living area square footage should be: <strong>${corrections.livingAreaSqft}</strong> sq ft` : '',
    corrections.buildingSqft ? `Building square footage should be: <strong>${corrections.buildingSqft}</strong> sq ft` : '',
    corrections.garageSize ? `Garage size should be: <strong>${corrections.garageSize}</strong> sq ft` : '',
    corrections.errorDescription ? String(corrections.errorDescription) : '',
  ].filter(Boolean);
  const correctionsBlock = correctionItems.length
    ? `<p style="font-size:10px;color:#333;line-height:1.7;margin-bottom:8px;text-align:justify;">I also believe the assessor records contain the following inaccuracies:</p>
    <ul style="padding-left:20px;margin-bottom:14px;">
      ${correctionItems.map((item) => `<li style="font-size:10px;color:#444;line-height:1.8;">${item}</li>`).join('')}
    </ul>`
    : '';

  return `<div style="page-break-after:always;background:white;font-family:Arial,Helvetica,sans-serif;color:${TEXT};height:calc(297mm - 1px);overflow:hidden;display:flex;flex-direction:column;box-sizing:border-box;">
  ${pageHeader(`Page 1 of ${totalPages}`)}
  <div style="flex:1;overflow:hidden;padding:35px 55px 20px;">
    <p style="color:${ACCENT};font-size:11px;margin-bottom:18px;">${today}</p>
    <p style="font-weight:bold;font-size:11px;margin-bottom:3px;">${officeName}</p>
    ${addressLines.map((l) => `<p style="font-size:10px;color:#555;margin-bottom:2px;">${l}</p>`).join('')}
    <div style="border:1px solid #ddd;margin:22px 0;">
      <div style="padding:10px 15px;border-bottom:2px solid ${ACCENT};"><span style="color:${ACCENT};font-weight:bold;font-size:12px;">RE: Property Tax Valuation Appeal</span></div>
      <table style="width:100%;border-collapse:collapse;">
        <tr>
          <td style="padding:10px 15px;font-size:10px;width:50%;"><span style="color:#888;display:block;font-size:9px;margin-bottom:2px;">Property:</span><strong>${data.address}</strong></td>
          <td style="padding:10px 15px;font-size:10px;width:50%;">
            ${apn ? `<span style="color:#888;display:block;font-size:9px;margin-bottom:2px;">APN:</span><strong>${apn}${legalDesc ? '<br><span style="font-weight:normal;font-size:9px;">' + legalDesc + '</span>' : ''}</strong>` : ''}
          </td>
        </tr>
      </table>
    </div>
    <p style="font-size:10px;color:#333;margin-bottom:14px;">Dear ${officeName},</p>
    <p style="font-size:10px;color:#333;line-height:1.7;margin-bottom:14px;text-align:justify;">I am writing to formally appeal the assessed value of the property at <strong>${data.address}</strong>${apn ? ` (APN: <strong>${apn}</strong>)` : ''}. I believe the current assessed value does not accurately reflect the property&apos;s fair market value based on a thorough analysis of recent comparable sales.</p>
    <p style="font-size:10px;color:#333;line-height:1.7;margin-bottom:14px;text-align:justify;">Based on a comprehensive review of comparable sales &mdash; with appropriate adjustments for differences in size, condition, location, features, and date of sale &mdash; I have determined that the estimated fair market value of this property is <strong>${fmv}</strong>. Supporting comparable sales data and adjustment analyses are enclosed with this appeal.</p>
    ${propDescription}
    ${correctionsBlock}
    <p style="font-size:10px;color:#333;line-height:1.7;margin-bottom:22px;text-align:justify;">I respectfully request that you review the enclosed analysis and consider adjusting the assessed value accordingly. Please contact me if you require additional documentation or clarification.</p>
    <p style="font-size:10px;color:#333;margin-bottom:35px;">Thank you for your time and consideration.</p>
    <p style="font-size:10px;color:#333;margin-bottom:45px;">Sincerely,</p>
    <p style="font-size:11px;font-weight:bold;color:${TEXT};margin-bottom:3px;">${data.ownerName}</p>
    ${data.ownerEmail ? `<p style="font-size:10px;color:#555;margin-bottom:2px;">${data.ownerEmail}</p>` : ''}
    ${data.ownerPhone ? `<p style="font-size:10px;color:#555;margin-bottom:2px;">${data.ownerPhone}</p>` : ''}
    <p style="font-size:10px;color:#555;">${data.address}</p>
  </div>
  ${pageFooter()}
</div>`;
}

function subjectPropertyPage(
  data: AppealPdfData,
  pageNum: number,
  totalPages: number,
): string {
  const fmv = data.marketFairValue ?? formatCurrency(data.finalValuation);
  const propInfo = data.propertyInfo ?? {};
  const apn = data.staticAppeal?.apn ?? '';
  const legalDesc = data.staticAppeal?.legalDescription ?? '';

  const lotSize = propInfo.lotSize ?? propInfo.LotSizeArea ?? null;
  const lotUnit = propInfo.lotUnit ?? 'sq ft';
  const livingArea = propInfo.livingArea ?? propInfo.LivingArea ?? null;
  const bedrooms = propInfo.bedrooms ?? propInfo.BedroomsTotal ?? null;
  const bathrooms =
    propInfo.bathrooms ?? propInfo.BathroomsTotalInteger ?? null;
  const yearBuilt = propInfo.yearBuilt ?? propInfo.YearBuilt ?? null;
  const garage = propInfo.garage ?? propInfo.GarageSpaces ?? null;
  const finishedBasement =
    propInfo.finishedBasement ?? propInfo.BasementFinishedArea ?? null;

  const photos = (data.images ?? []).slice(0, 4);

  const detailCells = [
    lotSize !== null
      ? detailCell('Lot Size', `${Number(lotSize).toLocaleString()} ${lotUnit}`)
      : '',
    livingArea !== null
      ? detailCell(
          'Living Area (above grade)',
          `${Number(livingArea).toLocaleString()} sq ft`,
        )
      : '',
    bedrooms !== null ? detailCell('Bedrooms', String(bedrooms)) : '',
    bathrooms !== null ? detailCell('Bathrooms', String(bathrooms)) : '',
    yearBuilt !== null ? detailCell('Year Built', String(yearBuilt)) : '',
    garage !== null ? detailCell('Garage', String(garage)) : '',
    finishedBasement !== null
      ? detailCell(
          'Finished Basement',
          `${Number(finishedBasement).toLocaleString()} sq ft`,
        )
      : '',
  ].filter(Boolean);

  const hasDetails = detailCells.length > 0;

  return `<div style="page-break-after:always;background:white;font-family:Arial,Helvetica,sans-serif;color:${TEXT};height:calc(297mm - 1px);overflow:hidden;display:flex;flex-direction:column;box-sizing:border-box;">
  ${pageHeader(`Page ${pageNum} of ${totalPages}`)}
  <div style="flex:1;overflow:hidden;padding:32px 55px 20px;">
    <h2 style="font-size:24px;font-weight:bold;color:${TEXT};border-bottom:3px solid ${ACCENT};padding-bottom:7px;margin-bottom:5px;">Subject Property</h2>
    <p style="color:#777;font-size:10px;margin-bottom:18px;">Property details, features, and estimated fair market value.</p>
    ${sectionTitle('Owner &amp; Contact')}
    <table style="width:100%;border-collapse:collapse;margin-bottom:15px;">
      <thead><tr><th style="text-align:left;font-size:8px;color:#999;font-weight:normal;padding:2px 0;width:33%;">Owner Name</th><th style="text-align:left;font-size:8px;color:#999;font-weight:normal;padding:2px 0;width:33%;">Email</th><th style="text-align:left;font-size:8px;color:#999;font-weight:normal;padding:2px 0;width:33%;">Phone</th></tr></thead>
      <tbody><tr><td style="font-size:11px;font-weight:bold;padding:5px 0;">${data.ownerName}</td><td style="font-size:10px;padding:5px 0;">${data.ownerEmail ?? ''}</td><td style="font-size:10px;padding:5px 0;">${data.ownerPhone ?? ''}</td></tr></tbody>
    </table>
    <div style="border-top:1px solid #eee;margin-bottom:5px;"></div>
    ${sectionTitle('Property Address &amp; Legal')}
    <table style="width:100%;border-collapse:collapse;margin-bottom:15px;">
      <tbody>
        <tr><td style="font-size:8px;color:#999;padding:2px 0 2px;width:18%;">Property Address</td><td style="font-size:11px;font-weight:bold;padding:2px 0 8px;">${data.address}</td></tr>
        ${apn ? `<tr><td style="font-size:8px;color:#999;padding:2px 0 2px;">APN</td><td style="font-size:11px;font-weight:bold;padding:2px 0 8px;">${apn}</td></tr>` : ''}
        ${legalDesc ? `<tr><td style="font-size:8px;color:#999;padding:2px 0 2px;">Legal Description</td><td style="font-size:11px;font-weight:bold;padding:2px 0 8px;">${legalDesc}</td></tr>` : ''}
      </tbody>
    </table>
    ${
      hasDetails
        ? `
    <div style="border-top:1px solid #eee;margin-bottom:5px;"></div>
    ${sectionTitle('Property Details')}
    <div style="display:flex;flex-wrap:wrap;gap:15px 0;margin-bottom:18px;">
      ${detailCells.map((c) => `<div style="width:25%;">${c}</div>`).join('')}
    </div>`
        : ''
    }
    ${
      photos.length > 0
        ? `
    <div style="border-top:1px solid #eee;margin-bottom:5px;"></div>
    ${sectionTitle('Property Photos')}
    ${photosGrid(photos)}`
        : ''
    }
    <div style="border:2px solid ${ACCENT};padding:22px;text-align:center;margin-top:16px;">
      <p style="font-size:10px;color:#888;margin-bottom:8px;">Estimated Fair Market Value</p>
      <p style="font-size:38px;font-weight:bold;color:${ACCENT};margin:4px 0;">${fmv}</p>
      <p style="font-size:9px;color:#888;font-style:italic;margin-top:8px;">Based on comprehensive analysis of comparable sales with adjustments for size, features, location, condition, and date of sale.</p>
    </div>
  </div>
  ${pageFooter()}
</div>`;
}

function comparablePage(
  comp: ComparableData,
  index: number,
  pageNum: number,
  totalPages: number,
): string {
  const photos = getPhotos(comp.mls, 4);
  const soldPrice = comp.soldPrice ?? 0;
  const condNum = parseAdjNum(comp.conditionAdjustment);
  const locNum = parseAdjNum(comp.locationAdjustment);
  const garNum = parseAdjNum(comp.garageAdjustment);
  const dateNum = parseAdjNum(comp.dateAdjustment);
  const specNum = comp.adjustmentValue ?? 0;
  const areaNum = comp.livingAreaAdjustment ?? 0;
  const totalAdj = condNum + locNum + garNum + dateNum + specNum + areaNum;
  const finalValue = soldPrice + totalAdj;

  const condAdj = formatAdj(condNum);
  const locAdj = formatAdj(locNum);
  const garAdj = formatAdj(garNum);
  const dateAdj = formatAdj(dateNum);
  const areaAdj = areaNum !== 0 ? formatAdj(areaNum) : '—';

  const hasSpecAdj = specNum !== 0;
  const adjColor = totalAdj > 0 ? '#27ae60' : totalAdj < 0 ? '#e74c3c' : '#999';
  const specDisplay = hasSpecAdj ? formatAdj(specNum) : '—';

  const garageDisplay = comp.garageSpaces
    ? String(comp.garageSpaces)
    : comp.garageType
      ? comp.garageType
      : '—';

  const detailItems = [
    comp.livingArea
      ? detailCell('Living Area', `${comp.livingArea.toLocaleString()} sq ft`)
      : '',
    comp.yearBuilt ? detailCell('Year Built', String(comp.yearBuilt)) : '',
    comp.lotSize
      ? detailCell(
          'Lot Size',
          `${comp.lotSize.toLocaleString()} ${comp.lotUnit ?? 'sq ft'}`,
        )
      : '',
    comp.bedrooms !== undefined
      ? detailCell('Bedrooms', String(comp.bedrooms))
      : '',
    comp.bathrooms !== undefined
      ? detailCell('Bathrooms', String(comp.bathrooms))
      : '',
    comp.garageSpaces !== undefined || comp.garageType
      ? detailCell('Garage Spaces', garageDisplay)
      : '',
  ].filter(Boolean);

  return `<div style="page-break-after:always;background:white;font-family:Arial,Helvetica,sans-serif;color:${TEXT};height:calc(297mm - 1px);overflow:hidden;display:flex;flex-direction:column;box-sizing:border-box;">
  ${pageHeader(`Page ${pageNum} of ${totalPages}`)}
  <div style="flex:1;overflow:hidden;padding:28px 55px 20px;">
    <p style="color:${ACCENT};font-size:9px;font-weight:bold;letter-spacing:1px;margin-bottom:4px;">COMPARABLE SALE #${index + 1}</p>
    <h2 style="font-size:20px;font-weight:bold;color:${TEXT};border-bottom:3px solid ${ACCENT};padding-bottom:6px;margin-bottom:14px;">${comp.address ?? 'N/A'}</h2>
    ${photosGrid(photos)}
    ${
      detailItems.length > 0
        ? `
    <div style="display:flex;flex-wrap:wrap;border-top:1px solid #eee;border-bottom:1px solid #eee;padding:10px 0;margin-bottom:16px;">
      ${detailItems.map((c) => `<div style="width:25%;">${c}</div>`).join('')}
    </div>`
        : ''
    }
    ${sectionTitle('Adjustment Summary')}
    <table style="width:100%;border-collapse:collapse;margin-bottom:12px;font-size:10px;">
      <tr style="background:#f8f8f8;border:1px solid #e0e0e0;">
        <td style="padding:9px 15px;font-weight:bold;width:72%;">Sold Price | Sold: ${formatSoldDate(comp.soldDate)}</td>
        <td style="padding:9px 15px;font-weight:bold;text-align:right;">${soldPrice ? formatCurrency(soldPrice) : 'N/A'}</td>
      </tr>
      <tr style="border-bottom:1px solid #eee;"><td style="padding:7px 15px;color:#555;">Condition</td><td style="padding:7px 15px;text-align:right;color:#999;">${condAdj}</td></tr>
      <tr style="border-bottom:1px solid #eee;"><td style="padding:7px 15px;color:#555;">Lot, Views &amp; Location</td><td style="padding:7px 15px;text-align:right;color:#999;">${locAdj}</td></tr>
      <tr style="border-bottom:1px solid #eee;"><td style="padding:7px 15px;color:#555;">Garage, Outbuildings &amp; Structures</td><td style="padding:7px 15px;text-align:right;color:#999;">${garAdj}</td></tr>
      <tr style="border-bottom:1px solid #eee;"><td style="padding:7px 15px;color:#555;">Date of Sale</td><td style="padding:7px 15px;text-align:right;color:#999;">${dateAdj}</td></tr>
      ${hasSpecAdj ? `<tr style="border-bottom:1px solid #eee;"><td style="padding:7px 15px;color:#555;">Special / AI Adjustment</td><td style="padding:7px 15px;text-align:right;color:${adjColor};font-weight:bold;">${specDisplay}</td></tr>` : ''}
      <tr style="border-bottom:1px solid #eee;"><td style="padding:7px 15px;color:#555;">Living Area</td><td style="padding:7px 15px;text-align:right;color:#999;">${areaAdj}</td></tr>
      <tr style="background:#f0f0f0;border:1px solid #ddd;">
        <td style="padding:9px 15px;font-weight:bold;">Total Adjustments</td>
        <td style="padding:9px 15px;text-align:right;font-weight:bold;color:${totalAdj !== 0 ? adjColor : '#999'};">${totalAdj !== 0 ? formatAdj(totalAdj) : '—'}</td>
      </tr>
    </table>
    ${comp.notes ? `<div style="background:#f8f8f8;border:1px solid #e0e0e0;padding:11px 15px;margin-bottom:14px;border-radius:3px;"><p style="font-size:9px;color:#666;font-style:italic;line-height:1.65;">${comp.notes}</p></div>` : ''}
    <div style="border:2px solid ${ACCENT};background:#fff8f8;padding:13px 20px;display:flex;justify-content:space-between;align-items:center;">
      <span style="color:${ACCENT};font-weight:bold;font-size:13px;">Final Adjusted Value</span>
      <span style="color:${ACCENT};font-size:24px;font-weight:bold;">${formatCurrency(finalValue)}</span>
    </div>
    <p style="text-align:center;font-size:8px;color:#aaa;margin-top:7px;">Subject: N/A | Comparable: ${comp.livingArea ? comp.livingArea.toLocaleString() + ' sq ft' : 'N/A'}</p>
  </div>
  ${pageFooter()}
</div>`;
}

export function generateAppealPdfHtml(data: AppealPdfData): string {
  const comparables: ComparableData[] = [
    ...(data.comparables ?? []),
    ...(data.aiComparables ?? []).filter((c) => c?.address),
  ];

  const totalPages = 1 + 1 + comparables.length;

  const pages: string[] = [
    instructionsPage(data),
    coverLetterPage(data, totalPages),
    subjectPropertyPage(data, 2, totalPages),
  ];

  comparables.forEach((comp, i) => {
    pages.push(comparablePage(comp, i, 3 + i, totalPages));
  });

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { background: white; }
    @page { size: A4; margin: 0; }
  </style>
</head>
<body>
${pages.join('\n')}
</body>
</html>`;
}
